import * as React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useDispatch } from 'react-redux';

import { DeleteQuestionEventAdapter } from '~analytics/adapters/DeleteQuestionEventAdapter';
import { BulkQuestionsUrlProvider } from '~teacher/components/QuizEditor/contexts/BulkQuestionsUrlProvider';
import { DeleteQuizUrlContextProvider } from '~teacher/components/QuizEditor/contexts/DeleteQuizUrlContextProvider';
import { QuizContextProvider } from '~teacher/components/QuizEditor/contexts/QuizContextProvider';
import { QuizQuestionOrderUrlProvider } from '~teacher/components/QuizEditor/contexts/QuizQuestionOrderUrlProvider';
import { QuizQuestionUrlProvider } from '~teacher/components/QuizEditor/contexts/QuizQuestionUrlProvider';
import { QuizUrlContextProvider } from '~teacher/components/QuizEditor/contexts/QuizUrlContextProvider';
import { UseBulkDeleteQuestionProvider } from '~teacher/components/QuizEditor/contexts/UseBulkDeleteQuestionProvider';
import QuizEditor, {
  deleteTypeAtom,
} from '~teacher/components/QuizEditor/QuizEditor';
import { PersonalQuizEditorHeader } from '~teacher/components/QuizEditor/QuizEditorHeader';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { quizzesQueryKey } from '~teacher/hook';
import { updateQuizQuestionOrder } from '~teacher/slices/quiz/quizSlice';

import {
  bulkQuestions,
  questionOrder,
  question as quizQuestionUrl,
} from '../../api/question';
import { uniqueIdFactory } from '../../api/quiz/getQuizzes/normalize/uniqueIds';
import { personalQuizUrl as quizUrl } from '../../api/urls';

interface Props {
  parentFolderId: number;
  controller: any;
  refreshQuestions: () => Promise<void>;
  quizId: number;
}

function useHandleOnSuccess() {
  const dispatch = useDispatch();
  const [deleteType] = useAtom(deleteTypeAtom);

  const handleOnSuccess = (results: any, { questionIds }) => {
    // If only temp questions were deleted, then no orders were returned.
    if (results) {
      dispatch(
        updateQuizQuestionOrder({
          questionOrderMap: results.orders,
        })
      );
    }

    // Analytics
    new DeleteQuestionEventAdapter({
      source: 'personal_library',
      deleteType: deleteType,
      questionsCount: questionIds.length,
    }).sendAnalytics();
  };
  return handleOnSuccess;
}

export function PersonalQuizPage({
  parentFolderId,
  controller,
  refreshQuestions,
  quizId,
}: Props) {
  const router = useSocrativeRouter();
  const queryClient = useQueryClient();

  const onSaveOrDeleteAndExit = () => {
    queryClient.invalidateQueries(quizzesQueryKey.quiz);
    const { lastRoute } = router;
    if (/^quizzes/.test(lastRoute)) {
      window.history.back();
    } else {
      const quizPath =
        parentFolderId != null
          ? `quizzes/${uniqueIdFactory.folder.toUniqueId(parentFolderId)}`
          : 'quizzes';
      router.navigate(quizPath, {
        trigger: true,
        replace: true,
      });
    }
  };
  const handleOnSuccess = useHandleOnSuccess();

  return (
    <QuizUrlContextProvider quizUrl={quizUrl}>
      <QuizQuestionUrlProvider quizQuestionUrl={quizQuestionUrl}>
        <QuizQuestionOrderUrlProvider quizQuestionOrderUrl={questionOrder}>
          <BulkQuestionsUrlProvider bulkQuestionsUrl={bulkQuestions}>
            <DeleteQuizUrlContextProvider deleteQuizUrl={quizUrl}>
              <UseBulkDeleteQuestionProvider
                createBody={(questionIds) => ({ questionIds: questionIds })}
                onSuccess={handleOnSuccess}
              >
                <QuizContextProvider quizId={quizId} quizType="personal">
                  <QuizEditor
                    refreshQuestions={refreshQuestions}
                    onSaveAndExit={onSaveOrDeleteAndExit}
                    onQuizDeleted={onSaveOrDeleteAndExit}
                    router={router}
                    quizHeaderProp={({
                      onHandleSaveAndExit,
                      isControlsDisabled,
                    }) => (
                      <PersonalQuizEditorHeader
                        editQuizController={controller}
                        isControlsDisabled={isControlsDisabled}
                        onHandleSaveAndExit={onHandleSaveAndExit}
                      />
                    )}
                  />
                </QuizContextProvider>
              </UseBulkDeleteQuestionProvider>
            </DeleteQuizUrlContextProvider>
          </BulkQuestionsUrlProvider>
        </QuizQuestionOrderUrlProvider>
      </QuizQuestionUrlProvider>
    </QuizUrlContextProvider>
  );
}
